import React, { useState, useEffect } from 'react';
import {
    Save,
    XCircle,
    CheckCircle,
    AlertTriangle,
    HardDrive,
    MapPin,
    Tag,
    Box,
    PlusCircle,
    Trash,
    Server,
    RadioTower,
    RefreshCw,
    Wifi,
    DoorOpen,
} from 'lucide-react';
import * as deviceApi from '../services/deviceApi';
import DeviceConnectionFields from './DeviceConnectionFields';
import { PANEL_CATALOG, BRAND_LABELS, BRAND_META } from './deviceCatalog';

const parseSlotDirection = (val) => {
    if (val === 2 || val === '2') return 2;
    const s = String(val || '').trim().toLowerCase();
    if (s === 'exit' || s === 'cikis' || s === 'çıkış') return 2;
    return 1;
};

const parseCardDataFormat = (val) => {
    if (val === 2 || val === '2') return 2;
    const s = String(val || '').trim().toLowerCase();
    if (s.startsWith('hex')) return 2;
    return 1;
};

const parseCardTechnology = (val) => {
    if (val === 1 || val === '1') return 1;
    const s = String(val || '').trim().toLowerCase();
    if (s.includes('prox') || s === 'em') return 1;
    return 2;
};

const parsePhysicalType = (val) => {
    if (val === 2 || val === '2') return 2;
    const s = String(val || '').trim().toLowerCase();
    if (s.includes('turnstile') || s.includes('turnike')) return 2;
    return 1;
};

const parseWiegandBits = (val) => {
    const n = Number(String(val ?? '').replace(/\D+/g, ''));
    if ([26, 34, 35, 66].includes(n)) return n;
    return 34;
};

function PanelEditForm({ panelData, onSave, onCancel }) {
    const [formData, setFormData] = useState({
        panelName: '',
        location: '',
        sourceName: '',
        brand: 'Hikvision',
        model: 'DS-K2804',
        serialNumber: '',
        macAddress: '',
        relayDurationSeconds: 3,
        cardDataFormat: 1,
        cardTechnology: 2,
        wiegandBitLength: 34,
        physicalType: 1, // 1 = Kapı (Door), 2 = Turnike (Turnstile)
        isActive: true,
    });

    const [conn, setConn] = useState({
        ipAddress: '',
        port: 80,
        useHttps: false,
        username: 'admin',
        password: '',
        protocol: 'ISAPI',
        sdkPort: 8000,
        verifyCertificate: false,
    });

    const [customModel, setCustomModel] = useState('');
    const [slots, setSlots] = useState([]);

    const [gatewaySources, setGatewaySources] = useState([]);
    const [loadingSources, setLoadingSources] = useState(false);

    const [testing, setTesting] = useState(false);
    const [fetchingInfo, setFetchingInfo] = useState(false);
    const [readerLoading, setReaderLoading] = useState(false);
    const [readerMsg, setReaderMsg] = useState('');
    const [feedback, setFeedback] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (panelData) {
            const resolvedBrand = panelData.brand || 'Hikvision';
            const catalogModels = PANEL_CATALOG[resolvedBrand] || [];
            const devModel = panelData.model || '';
            const isKnownModel = catalogModels.includes(devModel);
            const meta = BRAND_META[resolvedBrand] || BRAND_META.Hikvision;

            setFormData({
                panelName: panelData.panelName || '',
                location: panelData.location || '',
                sourceName: panelData.sourceName || '',
                brand: resolvedBrand,
                model: isKnownModel ? devModel : (devModel ? 'Diğer' : (catalogModels[0] || '')),
                serialNumber: panelData.serialNumber || '',
                macAddress: panelData.macAddress || '',
                relayDurationSeconds: panelData.relayDurationSeconds ?? 3,
                cardDataFormat: parseCardDataFormat(panelData.cardDataFormat),
                cardTechnology: parseCardTechnology(panelData.cardTechnology),
                wiegandBitLength: parseWiegandBits(panelData.wiegandBitLength),
                physicalType: parsePhysicalType(panelData.physicalType),
                isActive: panelData.isActive !== undefined ? Boolean(panelData.isActive) : true,
            });

            if (!isKnownModel && devModel) {
                setCustomModel(devModel);
            } else {
                setCustomModel('');
            }

            setConn({
                ipAddress: panelData.ipAddress || '',
                port: panelData.port ?? meta.defaultPort,
                useHttps: Boolean(panelData.useHttps),
                username: panelData.username || (resolvedBrand === 'Hikvision' ? 'admin' : ''),
                password: '',
                protocol: panelData.protocol || meta.defaultProtocol,
                sdkPort: panelData.sdkPort ?? 8000,
                verifyCertificate: Boolean(panelData.verifyCertificate),
            });

            if (Array.isArray(panelData.slots) && panelData.slots.length > 0) {
                setSlots(
                    panelData.slots.map((s) => ({
                        slotIndex: Number(s.slotIndex) || 1,
                        name: s.name || `Okuyucu ${s.slotIndex}`,
                        direction: parseSlotDirection(s.direction),
                        isActive: s.isActive !== undefined ? Boolean(s.isActive) : true,
                    }))
                );
            } else {
                setSlots([
                    { slotIndex: 1, name: 'Okuyucu 1 - Giriş', direction: 1, isActive: true },
                    { slotIndex: 2, name: 'Okuyucu 2 - Çıkış', direction: 2, isActive: true },
                ]);
            }
        }
        setFeedback(null);
        setReaderMsg('');
    }, [panelData]);

    useEffect(() => {
        let alive = true;
        const brand = formData.brand;
        (async () => {
            try {
                setLoadingSources(true);
                const list = await deviceApi.listGatewaySources(brand);
                if (!alive) return;
                setGatewaySources(Array.isArray(list) ? list : []);
            } catch {
                if (alive) setGatewaySources([]);
            } finally {
                if (alive) setLoadingSources(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [formData.brand]);

    const handleBrandChange = (newBrand) => {
        const models = PANEL_CATALOG[newBrand] || [];
        const meta = BRAND_META[newBrand] || BRAND_META.Hikvision;
        setFeedback(null);
        setCustomModel('');
        setFormData((prev) => ({
            ...prev,
            brand: newBrand,
            model: models.includes('DS-K2804') ? 'DS-K2804' : (models[0] || ''),
        }));
        setConn((prev) => ({
            ...prev,
            port: meta.defaultPort,
            protocol: meta.defaultProtocol,
            useHttps: false,
            verifyCertificate: false,
            username: meta.requiresAuth ? (prev.username || 'admin') : '',
            password: '',
            sdkPort: meta.defaultSdkPort || 8000,
        }));
    };

    const handleMainChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const formatMacInput = (raw) => {
        const clean = (raw || '').replace(/[^0-9A-Fa-f]/g, '').toUpperCase();
        const pairs = clean.match(/.{1,2}/g) || [];
        return pairs.slice(0, 6).join(':');
    };

    const handleSlotChange = (index, field, value) => {
        setSlots((prev) => {
            const copy = [...prev];
            copy[index] = {
                ...copy[index],
                [field]: field === 'slotIndex' || field === 'direction' ? Number(value) : value,
            };
            return copy;
        });
    };

    const addSlot = () => {
        if (slots.length >= 4) return;
        const usedIndices = slots.map((s) => s.slotIndex);
        let nextIndex = 1;
        for (let i = 1; i <= 4; i++) {
            if (!usedIndices.includes(i)) {
                nextIndex = i;
                break;
            }
        }
        setSlots((prev) => [
            ...prev,
            {
                slotIndex: nextIndex,
                name: `Okuyucu ${nextIndex} - ${nextIndex % 2 === 1 ? 'Giriş' : 'Çıkış'}`,
                direction: nextIndex % 2 === 1 ? 1 : 2,
                isActive: true,
            },
        ]);
    };

    const removeSlot = (index) => {
        if (slots.length <= 1) {
            setFeedback({ type: 'warning', text: 'Panelde en az 1 okuyucu slotu bulunmalıdır.' });
            return;
        }
        setSlots((prev) => prev.filter((_, idx) => idx !== index));
    };

    const handleTestConnection = async () => {
        setFeedback(null);
        if (!conn.ipAddress?.trim()) {
            setFeedback({ type: 'warning', text: 'Test için Panel IP Adresini girin.' });
            return;
        }
        try {
            setTesting(true);
            const res = await deviceApi.testConnection({
                deviceId: panelData?.id || null,
                brand: formData.brand,
                model: formData.model === 'Diğer' ? customModel : formData.model,
                ipAddress: conn.ipAddress.trim(),
                port: Number(conn.port) || 80,
                useHttps: Boolean(conn.useHttps),
                username: conn.username?.trim() || null,
                password: conn.password || null,
                protocol: conn.protocol || 'ISAPI',
                sdkPort: Number(conn.sdkPort) || 8000,
                verifyCertificate: Boolean(conn.verifyCertificate),
            });
            const ok = Boolean(res?.reachable && res?.authenticated);
            if (res?.macAddress || res?.serialNumber) {
                setFormData((prev) => ({
                    ...prev,
                    macAddress: res.macAddress || prev.macAddress,
                    serialNumber: prev.serialNumber || res.serialNumber || '',
                }));
            }
            setFeedback({
                type: ok ? 'success' : (res?.reachable ? 'warning' : 'danger'),
                text: res?.message || (ok ? 'Panel bağlantısı başarılı.' : 'Panel bağlantı testi başarısız.'),
            });
        } catch (err) {
            setFeedback({
                type: 'danger',
                text: err?.response?.data?.message || err?.message || 'Bağlantı testi sırasında hata oluştu.',
            });
        } finally {
            setTesting(false);
        }
    };

    const handleFetchDeviceInfo = async () => {
        setFeedback(null);
        if (!conn.ipAddress?.trim()) {
            setFeedback({ type: 'warning', text: 'Panel bilgilerini çekmek için IP adresini girin.' });
            return;
        }
        try {
            setFetchingInfo(true);
            const info = await deviceApi.fetchDeviceInfo({
                deviceId: panelData?.id || null,
                ipAddress: conn.ipAddress.trim(),
                port: Number(conn.port) || (conn.useHttps ? 443 : 80),
                useHttps: Boolean(conn.useHttps),
                username: conn.username?.trim() || 'admin',
                password: conn.password || null,
                protocol: conn.protocol || 'ISAPI',
                sdkPort: Number(conn.sdkPort) || 8000,
                verifyCertificate: Boolean(conn.verifyCertificate),
            });

            if (!info?.success) {
                setFeedback({
                    type: 'danger',
                    text: info?.message || 'Panel bilgileri alınamadı.',
                });
                return;
            }

            const catalogModels = PANEL_CATALOG[formData.brand] || [];
            const fetchedModel = (info.model || '').trim();
            const isKnown = catalogModels.includes(fetchedModel);

            setFormData((prev) => ({
                ...prev,
                panelName: prev.panelName || info.deviceName || '',
                serialNumber: info.serialNumber || prev.serialNumber,
                macAddress: info.macAddress || prev.macAddress,
                model: fetchedModel ? (isKnown ? fetchedModel : 'Diğer') : prev.model,
                relayDurationSeconds: info.relayDurationSeconds ?? prev.relayDurationSeconds,
                wiegandBitLength: info.wiegandBitLength ? Number(info.wiegandBitLength) : prev.wiegandBitLength,
            }));

            if (fetchedModel && !isKnown) {
                setCustomModel(fetchedModel);
            }

            setFeedback({
                type: 'success',
                text: `${info.message || 'Panel bilgileri alındı.'}${info.serialNumber ? ` (Seri No: ${info.serialNumber})` : ''}`,
            });
        } catch (err) {
            setFeedback({
                type: 'danger',
                text: err?.response?.data?.message || err?.message || 'Panel bilgisi çekilirken hata oluştu.',
            });
        } finally {
            setFetchingInfo(false);
        }
    };

    const handleReadReaderSettings = async () => {
        if (!panelData?.id) return;
        setReaderMsg('');
        try {
            setReaderLoading(true);
            const r = await deviceApi.getReaderSettings(panelData.id, {
                username: conn.username,
                password: conn.password || undefined,
                useHttps: conn.useHttps,
                port: conn.port,
                verifyCertificate: conn.verifyCertificate,
                protocol: conn.protocol,
            });
            setFormData((prev) => ({
                ...prev,
                wiegandBitLength: Number(r?.wiegandBitLength || prev.wiegandBitLength || 34),
                cardTechnology: Number(r?.cardTechnology || prev.cardTechnology || 2),
                cardDataFormat: Number(r?.cardDataFormat || prev.cardDataFormat || 1),
            }));
            setReaderMsg(`Okundu (${r?.source === 'device' ? 'Panel ISAPI' : r?.source === 'sdk' ? 'Panel SDK' : 'Veritabanı'}).`);
        } catch (err) {
            setReaderMsg(err?.response?.data?.message || 'Panelden kart ayarları okunamadı.');
        } finally {
            setReaderLoading(false);
        }
    };

    const handleWriteReaderSettings = async () => {
        if (!panelData?.id) return;
        setReaderMsg('');
        try {
            setReaderLoading(true);
            const res = await deviceApi.updateReaderSettings(panelData.id, {
                wiegandBitLength: Number(formData.wiegandBitLength),
                cardTechnology: Number(formData.cardTechnology),
                cardDataFormat: Number(formData.cardDataFormat),
            });
            setReaderMsg(res?.message || 'Kart okuyucu ayarları kaydedildi.');
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = err?.response?.data?.message || 'Kart okuyucu ayarları yazılamadı.';
            setReaderMsg(detail ? `${msg} (${detail})` : msg);
        } finally {
            setReaderLoading(false);
        }
    };

    const handleOpenSlotDoor = async (slotIndex) => {
        if (!panelData?.id) return;
        setFeedback(null);
        try {
            const res = await deviceApi.openDoorById(panelData.id, {
                password: conn.password || null,
                doorNo: Number(slotIndex) || 1,
                durationSeconds: Number(formData.relayDurationSeconds || 3),
            });
            setFeedback({
                type: res?.success ? 'success' : 'warning',
                text: res?.message
                    ? `Slot ${slotIndex}: ${res.message}`
                    : `Slot ${slotIndex} röle komutu gönderildi.`,
            });
        } catch (err) {
            setFeedback({
                type: 'danger',
                text: err?.response?.data?.message || `Slot ${slotIndex} kapı açma hatası.`,
            });
        }
    };

    const validateForm = () => {
        if (!formData.panelName.trim()) return 'Panel adı zorunludur.';
        if (!formData.sourceName?.trim()) return 'Gateway Servisi (Source Name) seçimi zorunludur.';
        if (!formData.serialNumber?.trim()) return 'Panel seri numarası zorunludur.';

        const meta = BRAND_META[formData.brand] || BRAND_META.Hikvision;
        if (formData.brand === 'Hikvision' && !conn.ipAddress?.trim()) {
            return 'Panel IP adresi zorunludur.';
        }
        if (meta.requiresAuth && !conn.username?.trim()) {
            return 'Panel kullanıcı adı zorunludur.';
        }

        const indices = slots.map((s) => s.slotIndex);
        if (new Set(indices).size !== indices.length) {
            return 'Okuyucu slot numaraları (1..4) mükerrer olamaz.';
        }
        for (const s of slots) {
            if (!s.name?.trim()) return `Slot ${s.slotIndex} için okuyucu adı girilmelidir.`;
        }
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFeedback(null);
        const err = validateForm();
        if (err) {
            setFeedback({ type: 'danger', text: err });
            return;
        }

        const resolvedModel = formData.model === 'Diğer' ? (customModel.trim() || 'Diğer') : formData.model;

        const payload = {
            panelName: formData.panelName.trim(),
            location: formData.location?.trim() || null,
            sourceName: formData.sourceName.trim(),
            brand: formData.brand,
            model: resolvedModel,
            serialNumber: formData.serialNumber.trim(),
            macAddress: formData.macAddress?.trim() || null,
            ipAddress: conn.ipAddress?.trim() || '',
            port: Number(conn.port) || 80,
            useHttps: Boolean(conn.useHttps),
            username: conn.username?.trim() || null,
            password: conn.password?.trim() || null,
            protocol: conn.protocol || 'ISAPI',
            sdkPort: Number(conn.sdkPort) || 8000,
            verifyCertificate: Boolean(conn.verifyCertificate),
            relayDurationSeconds: Number(formData.relayDurationSeconds || 3),
            cardDataFormat: parseCardDataFormat(formData.cardDataFormat),
            cardTechnology: parseCardTechnology(formData.cardTechnology),
            wiegandBitLength: parseWiegandBits(formData.wiegandBitLength),
            physicalType: parsePhysicalType(formData.physicalType),
            isActive: Boolean(formData.isActive),
            slots: slots.map((s) => ({
                slotIndex: Number(s.slotIndex) || 1,
                name: s.name.trim(),
                direction: parseSlotDirection(s.direction),
                isActive: Boolean(s.isActive),
            })),
        };

        try {
            setSubmitting(true);
            await onSave(panelData.id, payload);
        } catch (error) {
            const msg = error?.response?.data?.message || error?.message || 'Panel güncellenirken bir hata oluştu.';
            setFeedback({ type: 'danger', text: msg });
        } finally {
            setSubmitting(false);
        }
    };

    const isHik = formData.brand === 'Hikvision';
    const isXpass = formData.brand === 'Xpass';
    const brandMeta = BRAND_META[formData.brand] || BRAND_META.Hikvision;
    const availableModels = PANEL_CATALOG[formData.brand] || [];

    const inputClass =
        'w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-all';
    const labelClass = 'block text-[11px] font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300 mb-1';
    const sectionClass =
        'p-3.5 rounded-xl border border-gray-200 dark:border-gray-700/80 bg-gray-50/70 dark:bg-gray-900/40 space-y-2.5';

    const feedbackStyles = {
        success: 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-300 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200',
        danger: 'bg-rose-50 dark:bg-rose-950/40 border-rose-300 dark:border-rose-800 text-rose-800 dark:text-rose-200',
        warning: 'bg-amber-50 dark:bg-amber-950/40 border-amber-300 dark:border-amber-800 text-amber-800 dark:text-amber-200',
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-3.5">
            {feedback && (
                <div
                    className={`px-3.5 py-2.5 rounded-xl border flex items-center justify-between gap-3 ${
                        feedbackStyles[feedback.type] || feedbackStyles.danger
                    }`}
                >
                    <div className="flex items-center gap-2 text-xs font-semibold">
                        {feedback.type === 'success' ? (
                            <CheckCircle className="w-4 h-4 shrink-0" />
                        ) : feedback.type === 'warning' ? (
                            <AlertTriangle className="w-4 h-4 shrink-0" />
                        ) : (
                            <XCircle className="w-4 h-4 shrink-0" />
                        )}
                        <span>{feedback.text}</span>
                    </div>
                    <button
                        type="button"
                        onClick={() => setFeedback(null)}
                        className="text-xs font-bold opacity-75 hover:opacity-100"
                    >
                        ✕
                    </button>
                </div>
            )}

            {/* 1. ÜST BAR: PANEL MARKASI SEÇİMİ + PANEL AKTİF */}
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 flex-1">
                    {Object.keys(PANEL_CATALOG).map((bKey) => {
                        const active = formData.brand === bKey;
                        return (
                            <button
                                key={bKey}
                                type="button"
                                onClick={() => handleBrandChange(bKey)}
                                className={`px-3.5 py-2 rounded-xl text-xs font-bold border transition-all flex items-center justify-center gap-2 ${
                                    active
                                        ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm shadow-indigo-500/20'
                                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-indigo-400'
                                }`}
                            >
                                <Server className="w-3.5 h-3.5" />
                                <span>{BRAND_LABELS[bKey] || bKey} Çoklu Okuyucu Paneli</span>
                            </button>
                        );
                    })}
                </div>

                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 cursor-pointer select-none">
                    <input
                        type="checkbox"
                        name="isActive"
                        className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 border-gray-300 dark:border-gray-600"
                        checked={formData.isActive}
                        onChange={handleMainChange}
                    />
                    <span className="text-xs font-bold text-gray-800 dark:text-gray-200">Panel Aktif</span>
                </label>
            </div>

            {/* 2. ÜST 2 SÜTUNLU ALAN: SOLDA TEMEL & DONANIM, SAĞDA AĞ & WIEGAND */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5 items-start">
                {/* SOL SÜTUN: PANEL TEMEL & DONANIM BİLGİLERİ */}
                <div className={sectionClass}>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5">
                        <HardDrive className="w-3.5 h-3.5" />
                        Panel Temel &amp; Donanım Bilgileri
                    </h4>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        <div>
                            <label className={labelClass}>
                                Panel Adı <span className="text-rose-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="panelName"
                                className={inputClass}
                                value={formData.panelName}
                                onChange={handleMainChange}
                                required
                            />
                        </div>

                        <div>
                            <label className={labelClass}>
                                <MapPin className="inline w-3 h-3 mr-1 text-indigo-500" />
                                Konum / Pano Yeri
                            </label>
                            <input
                                type="text"
                                name="location"
                                className={inputClass}
                                value={formData.location}
                                onChange={handleMainChange}
                            />
                        </div>

                        <div>
                            <label className={labelClass}>
                                <Box className="inline w-3 h-3 mr-1 text-indigo-500" />
                                Panel Modeli
                            </label>
                            <select
                                name="model"
                                className={inputClass}
                                value={formData.model}
                                onChange={handleMainChange}
                            >
                                {availableModels.map((m) => (
                                    <option key={m} value={m}>
                                        {m}
                                    </option>
                                ))}
                            </select>
                            {formData.model === 'Diğer' && (
                                <input
                                    type="text"
                                    className={`${inputClass} mt-1.5`}
                                    placeholder="Örn: DS-K2804"
                                    value={customModel}
                                    onChange={(e) => setCustomModel(e.target.value)}
                                />
                            )}
                        </div>

                        <div>
                            <label className={labelClass}>
                                <Tag className="inline w-3 h-3 mr-1 text-indigo-500" />
                                Gateway Servisi <span className="text-rose-500">*</span>
                            </label>
                            <select
                                name="sourceName"
                                className={inputClass}
                                value={formData.sourceName}
                                onChange={handleMainChange}
                                disabled={loadingSources}
                                required
                            >
                                <option value="">
                                    {loadingSources ? 'Yükleniyor...' : 'Gateway seçin...'}
                                </option>
                                {gatewaySources.map((x) => (
                                    <option key={`${x.serviceId}-${x.sourceName}`} value={x.sourceName}>
                                        {x.sourceName} ({x.vendor})
                                    </option>
                                ))}
                                {formData.sourceName &&
                                    !gatewaySources.some((x) => x.sourceName === formData.sourceName) && (
                                        <option value={formData.sourceName}>
                                            {formData.sourceName} (Mevcut)
                                        </option>
                                    )}
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 pt-1">
                        <div>
                            <label className={labelClass}>
                                Panel Seri No <span className="text-rose-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="serialNumber"
                                className={`${inputClass} font-mono`}
                                value={formData.serialNumber}
                                onChange={(e) => {
                                    if (isXpass) {
                                        const formatted = formatMacInput(e.target.value);
                                        setFormData((prev) => ({
                                            ...prev,
                                            serialNumber: formatted,
                                            macAddress: formatted,
                                        }));
                                    } else {
                                        handleMainChange(e);
                                    }
                                }}
                                required
                            />
                        </div>

                        <div>
                            <label className={labelClass}>MAC Adresi</label>
                            <input
                                type="text"
                                name="macAddress"
                                className={`${inputClass} font-mono`}
                                placeholder="AA:BB:CC:DD:EE:FF"
                                value={formData.macAddress || ''}
                                onChange={(e) => {
                                    const formatted = formatMacInput(e.target.value);
                                    setFormData((prev) => ({
                                        ...prev,
                                        macAddress: formatted,
                                        ...(isXpass ? { serialNumber: formatted } : {}),
                                    }));
                                }}
                            />
                        </div>

                        <div>
                            <label className={labelClass}>Röle Süresi (Sn)</label>
                            <input
                                type="number"
                                className={inputClass}
                                min={1}
                                max={255}
                                value={formData.relayDurationSeconds}
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        relayDurationSeconds: Math.max(1, Math.min(255, Number(e.target.value || 3))),
                                    }))
                                }
                            />
                        </div>
                    </div>

                    {/* FİZİKSEL TİP (SEÇİMLİ BUTONLAR) */}
                    <div className="pt-1">
                        <label className={labelClass}>Panel Fiziksel Kullanım Tipi</label>
                        <div className="flex p-0.5 rounded-lg bg-gray-200/80 dark:bg-gray-800 border border-gray-300/60 dark:border-gray-700">
                            <button
                                type="button"
                                onClick={() => setFormData((prev) => ({ ...prev, physicalType: 1 }))}
                                className={`flex-1 py-1.5 rounded-md text-xs font-bold transition-all ${
                                    Number(formData.physicalType) !== 2
                                        ? 'bg-indigo-600 text-white shadow-sm'
                                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                }`}
                            >
                                Kapı Kontrol Paneli
                            </button>
                            <button
                                type="button"
                                onClick={() => setFormData((prev) => ({ ...prev, physicalType: 2 }))}
                                className={`flex-1 py-1.5 rounded-md text-xs font-bold transition-all ${
                                    Number(formData.physicalType) === 2
                                        ? 'bg-indigo-600 text-white shadow-sm'
                                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                }`}
                            >
                                Turnike Kontrol Paneli
                            </button>
                        </div>
                    </div>
                </div>

                {/* SAĞ SÜTUN: AĞ BAĞLANTISI & WIEGAND AYARLARI */}
                <div className="space-y-3">
                    <div className="space-y-2">
                        <DeviceConnectionFields
                            value={conn}
                            onChange={setConn}
                            brand={formData.brand}
                            isEditing={true}
                        />

                        <div className="flex flex-wrap items-center gap-2">
                            <button
                                type="button"
                                onClick={handleTestConnection}
                                disabled={testing}
                                className="px-3 py-1.5 rounded-lg text-xs font-bold bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/40 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 transition-all flex items-center gap-1.5 disabled:opacity-50"
                            >
                                <Wifi className="w-3.5 h-3.5" />
                                {testing ? 'Test Ediliyor...' : 'Panel Bağlantısını Test Et'}
                            </button>

                            {brandMeta.supportsIsapiFetch && (
                                <button
                                    type="button"
                                    onClick={handleFetchDeviceInfo}
                                    disabled={fetchingInfo}
                                    className="px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 transition-all flex items-center gap-1.5 disabled:opacity-50"
                                >
                                    <RefreshCw className={`w-3.5 h-3.5 ${fetchingInfo ? 'animate-spin' : ''}`} />
                                    {fetchingInfo
                                        ? 'Çekiliyor...'
                                        : 'Panel Bilgilerini Çek (Seri No / MAC / Model)'}
                                </button>
                            )}
                        </div>
                    </div>

                    {isHik && (
                        <div className={sectionClass}>
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5">
                                    <RadioTower className="w-3.5 h-3.5" />
                                    Wiegand &amp; Kart Okuyucu Formatı
                                </h4>

                                <div className="flex items-center gap-1.5">
                                    <button
                                        type="button"
                                        onClick={handleReadReaderSettings}
                                        disabled={readerLoading}
                                        className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-all disabled:opacity-50"
                                    >
                                        {readerLoading ? 'Okunuyor...' : 'Cihazdan Oku'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleWriteReaderSettings}
                                        disabled={readerLoading}
                                        className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-all disabled:opacity-50"
                                    >
                                        {readerLoading ? 'Yazılıyor...' : 'Panele & Sisteme Yaz'}
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                                <div>
                                    <label className={labelClass}>Kart Veri Formatı</label>
                                    <select
                                        className={inputClass}
                                        value={Number(formData.cardDataFormat || 1)}
                                        onChange={(e) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                cardDataFormat: Number(e.target.value),
                                            }))
                                        }
                                    >
                                        <option value={1}>Decimal (Onluk)</option>
                                        <option value={2}>Hexadecimal (Hex)</option>
                                    </select>
                                </div>

                                <div>
                                    <label className={labelClass}>Kart Teknolojisi</label>
                                    <select
                                        className={inputClass}
                                        value={Number(formData.cardTechnology || 2)}
                                        onChange={(e) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                cardTechnology: Number(e.target.value),
                                            }))
                                        }
                                    >
                                        <option value={2}>Mifare (13.56 MHz)</option>
                                        <option value={1}>EM / Proximity</option>
                                    </select>
                                </div>

                                <div>
                                    <label className={labelClass}>Wiegand Bit</label>
                                    <select
                                        className={inputClass}
                                        value={Number(formData.wiegandBitLength || 34)}
                                        onChange={(e) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                wiegandBitLength: Number(e.target.value),
                                            }))
                                        }
                                    >
                                        <option value={26}>Wiegand 26 Bit</option>
                                        <option value={34}>Wiegand 34 Bit</option>
                                        <option value={35}>Wiegand 35 Bit</option>
                                        <option value={66}>Wiegand 66 Bit</option>
                                    </select>
                                </div>
                            </div>

                            {readerMsg && (
                                <p className="text-[11px] font-semibold text-indigo-600 dark:text-indigo-400">
                                    {readerMsg}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* 3. OKUYUCU SLOTLARI (1..4) - TAM GENİŞLİK KOMPAKT TABLO */}
            <div className={sectionClass}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                        <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5">
                            <Server className="w-3.5 h-3.5" />
                            Panel Okuyucu Slotları (Maksimum 4 Okuyucu: Slot 1..4)
                        </h4>
                    </div>

                    <button
                        type="button"
                        onClick={addSlot}
                        disabled={slots.length >= 4}
                        className="px-3 py-1 rounded-lg text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-all flex items-center gap-1 disabled:opacity-50"
                    >
                        <PlusCircle className="w-3.5 h-3.5" />
                        Okuyucu Slotu Ekle ({slots.length}/4)
                    </button>
                </div>

                <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                        <thead className="bg-gray-100/80 dark:bg-gray-700/60 font-bold uppercase text-gray-600 dark:text-gray-300">
                            <tr>
                                <th className="px-2.5 py-2 text-left w-36">Okuyucu Slotu</th>
                                <th className="px-2.5 py-2 text-left">Okuyucu / Geçiş Noktası Adı</th>
                                <th className="px-2.5 py-2 text-left w-48">Geçiş Yönü</th>
                                <th className="px-2.5 py-2 text-center w-16">Aktif</th>
                                {isHik && <th className="px-2.5 py-2 text-center w-24">Röle Test</th>}
                                <th className="px-2.5 py-2 text-center w-14">Sil</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {slots.map((slot, index) => (
                                <tr key={index}>
                                    <td className="px-2.5 py-1.5">
                                        <select
                                            className={inputClass}
                                            value={slot.slotIndex}
                                            onChange={(e) => handleSlotChange(index, 'slotIndex', e.target.value)}
                                        >
                                            <option value={1}>Slot 1 (Okuyucu 1)</option>
                                            <option value={2}>Slot 2 (Okuyucu 2)</option>
                                            <option value={3}>Slot 3 (Okuyucu 3)</option>
                                            <option value={4}>Slot 4 (Okuyucu 4)</option>
                                        </select>
                                    </td>
                                    <td className="px-2.5 py-1.5">
                                        <input
                                            type="text"
                                            className={inputClass}
                                            value={slot.name}
                                            onChange={(e) => handleSlotChange(index, 'name', e.target.value)}
                                            required
                                        />
                                    </td>
                                    <td className="px-2.5 py-1.5">
                                        <div className="flex p-0.5 rounded-lg bg-gray-200/80 dark:bg-gray-900 border border-gray-300/60 dark:border-gray-700">
                                            <button
                                                type="button"
                                                onClick={() => handleSlotChange(index, 'direction', 1)}
                                                className={`flex-1 py-1 rounded-md text-xs font-bold transition-all ${
                                                    Number(slot.direction) === 1
                                                        ? 'bg-emerald-600 text-white shadow-sm'
                                                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                                                }`}
                                            >
                                                Giriş
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleSlotChange(index, 'direction', 2)}
                                                className={`flex-1 py-1 rounded-md text-xs font-bold transition-all ${
                                                    Number(slot.direction) === 2
                                                        ? 'bg-rose-600 text-white shadow-sm'
                                                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                                                }`}
                                            >
                                                Çıkış
                                            </button>
                                        </div>
                                    </td>
                                    <td className="px-2.5 py-1.5 text-center">
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600"
                                            checked={slot.isActive}
                                            onChange={(e) => handleSlotChange(index, 'isActive', e.target.checked)}
                                        />
                                    </td>
                                    {isHik && (
                                        <td className="px-2.5 py-1.5 text-center">
                                            <button
                                                type="button"
                                                onClick={() => handleOpenSlotDoor(slot.slotIndex)}
                                                className="px-2 py-1 rounded-lg text-xs font-bold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 inline-flex items-center gap-1"
                                                title={`Kapı / Röle ${slot.slotIndex} Aç`}
                                            >
                                                <DoorOpen className="w-3 h-3" />
                                                Aç
                                            </button>
                                        </td>
                                    )}
                                    <td className="px-2.5 py-1.5 text-center">
                                        <button
                                            type="button"
                                            onClick={() => removeSlot(index)}
                                            disabled={slots.length <= 1}
                                            className="p-1 rounded-lg text-rose-500 hover:bg-rose-50 dark:hover:bg-gray-700 disabled:opacity-30 transition-colors"
                                            title="Slotu Kaldır"
                                        >
                                            <Trash className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* FOOTER BUTONLARI */}
            <div className="flex justify-end items-center gap-2.5 pt-2.5 border-t border-gray-200 dark:border-gray-700">
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={submitting}
                    className="px-4 py-2 rounded-xl text-xs font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
                >
                    İptal
                </button>
                <button
                    type="submit"
                    disabled={submitting}
                    className="px-5 py-2 rounded-xl text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 shadow-md shadow-indigo-500/20 transition-all flex items-center gap-1.5 disabled:opacity-50"
                >
                    <Save className="w-3.5 h-3.5" />
                    {submitting ? 'Güncelleniyor...' : 'Panel Değişikliklerini Kaydet'}
                </button>
            </div>
        </form>
    );
}

export default PanelEditForm;
